import { GraphQL, GraphQLPagedQueryData } from "@rpi/openapi-api";
import { CellRoute } from "../../components/cells/CellLink";
import { CopyableRow } from "../../components/CopyableRow";
import { RpiTableColumns } from "../../rpi-core/table/RpiTable";
import { createEntityColumn } from "../../rpi-core/table/RpiTableHelpers";
import { formatAuthHeader } from "../../utils/misc.util";

const apiCredentialColumn = createEntityColumn<GraphQL.ApiCredentialsResponse, GraphQL.ApiCredentialSearchFields>();

const truncatedCopyCell = (value: string) => (
  <CopyableRow value={value} overflow="none" size="sm">
    <span className="text-ellipsis">{value}</span>
  </CopyableRow>
);

export const ApiCredentialColumn = {
  description: apiCredentialColumn<"description">({
    header: "Description",
    accessor: "description",
    sortKey: GraphQL.ApiCredentialSearchFields.Description,
    size: 300,
  }),
  customer: apiCredentialColumn<"customerPublicId">({
    header: "Organization",
    accessor: "customerPublicId",
    sortKey: GraphQL.ApiCredentialSearchFields.CustomerPublicId,
    cell: ({ row }) => {
      if (!row.customerPublicId) return;
      return <CellRoute route={["customer", { publicId: row.customerPublicId }]} children={row.customerPublicId} />;
    },
    size: 200,
  }),
  apiKey: apiCredentialColumn<"apiKey">({
    header: "API Key",
    accessor: "apiKey",
    sortKey: GraphQL.ApiCredentialSearchFields.ApiKey,
    cell: ({ row }) => truncatedCopyCell(row.apiKey),
    size: 200,
  }),
  sharedSecret: apiCredentialColumn<"sharedSecret">({
    header: "Shared Secret",
    accessor: "sharedSecret",
    sortKey: GraphQL.ApiCredentialSearchFields.SharedSecret,
    cell: ({ row }) => truncatedCopyCell(row.sharedSecret),
    size: 200,
  }),
  authHeader: apiCredentialColumn<"apiKey" | "sharedSecret">({
    header: "Authorization Header",
    cell: ({ row }) => truncatedCopyCell(formatAuthHeader(row.apiKey, row.sharedSecret)),
    size: 250,
  }),
  // Intentionally not sortable: the webhook secret is an org-level value shared by every
  // credential in an organization, so ordering by it is meaningless within an org.
  webhookSecret: apiCredentialColumn<"webhookSecret">({
    header: "Webhook Secret",
    accessor: "webhookSecret",
    cell: ({ row }) => truncatedCopyCell(row.webhookSecret),
    size: 200,
  }),
};

export const apiCredentialColumns: RpiTableColumns<GraphQLPagedQueryData<GraphQL.ApiCredentialsQuery>> = (user) => [
  user.is.SUPER_ADMIN && ApiCredentialColumn.customer,
  user.is.SUPER_ADMIN && ApiCredentialColumn.webhookSecret,
  ApiCredentialColumn.apiKey,
  ApiCredentialColumn.sharedSecret,
  ApiCredentialColumn.authHeader,
  ApiCredentialColumn.description,
];
